/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  DestroyRef,
  Inject,
  SecurityContext,
  ViewEncapsulation,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormControl, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import {
  MatLegacyDialog as MatDialog,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { AuthService } from 'src/app/service/auth.service';
import { FeatureFlagsService } from 'src/app/shared/services/customer-api/feature-flags.service';
import { QueryApiService } from 'src/app/shared/services/customer-api/query-api.service';
import { RoutingService } from 'src/app/service/routing.service';
import { AiSearchService } from 'src/app/shared/services/ai-search/ai-search.service';
import * as FileSaver from 'file-saver';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { YukkaQuery } from 'src/app/shared/models/ai-search.interface';
import {
  prunedTree,
  queryCounts,
} from 'src/app/micro-components/query-builder/query-builder.model';
import {
  TermField,
  TermPickerOption,
} from 'src/app/micro-components/term-picker/term-picker.component';
import { PortfolioApiService } from 'src/app/shared/services/customer-api/portfolio-api.service';
import { Observable, combineLatest, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { writeSearchHandoff } from 'src/app/shared/utils/search-handoff';

/**
 * This component is used inside the query-folio component 4 edit the query itself
 */

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-query-editor',
  templateUrl: './query-editor.component.html',
  styleUrls: ['./query-editor.component.scss'],
})
export class QueryEditorComponent {
  /**
   * The query as the API states it. The builder edits it whole — this dialog
   * used to mirror the tree in nested FormArrays, which meant every shape the
   * API could return needed a matching control.
   */
  tree: YukkaQuery = { operator: 'and', filters: [], groups: [] };

  /** A collection query filters by topic, so events belong here (DD omits them). */
  readonly pickerFields: TermField[] = ['text', 'entity', 'event', 'source'];

  /**
   * The user's saved queries and portfolios, offered as pills so a query can
   * embed another. Best-effort: the picker still works without them.
   */
  readonly artifacts$: Observable<TermPickerOption[]> = combineLatest([
    this.queryApiList(),
    this.portfolioList(),
  ]).pipe(map(([queries, portfolios]) => [...queries, ...portfolios]));

  namequery: UntypedFormControl;
  descriptionQuery: UntypedFormControl;
  selectedCollection: any;
  qtext: boolean;
  query: string;
  $querylist: Observable<any>;
  insave: boolean;
  qparams: any;
  idelete = 0;
  isAiGenerated = false;

  isButtonActive = false;

  /**
   * A query with no term matches everything — nothing to save or export. Set
   * with the tree rather than derived per read: seven bindings ask for it, and
   * the dialog's three text inputs run change detection on every keystroke.
   */
  hasTerms = false;

  setTree(tree: YukkaQuery): void {
    this.tree = tree;
    this.hasTerms = queryCounts(tree).terms > 0;
  }

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog,
    public auth: AuthService,
    public featureFlagsService: FeatureFlagsService,
    public queryApi: QueryApiService,
    private portfolioApi: PortfolioApiService,
    public routing: RoutingService,
    private sanitizer: DomSanitizer,
    private aiSearchService: AiSearchService,
    @Inject(MAT_DIALOG_DATA) public data,
    private destroyRef: DestroyRef,
  ) {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((qparams) => {
        this.qparams = qparams;
      });
    this.selectedCollection = [];
    this.namequery = new UntypedFormControl('', [Validators.required]);
    this.descriptionQuery = new UntypedFormControl('');
    if (data) {
      this.isAiGenerated = !!data._isAiGenerated;
      // Embedded queries and portfolios stay in the arrays they live in — the
      // builder reads them from there and shows them beside the groups.
      this.setTree(data.query);
      this.namequery.setValue(data.name);
      this.descriptionQuery.setValue(data.description);
      this.selectedCollection = data.groups;
    } else {
      this.setTree({ operator: 'and', filters: [], groups: [] });
      this.selectedCollection.push(this.qparams.id);
    }
  }

  private queryApiList(): Observable<TermPickerOption[]> {
    return this.queryApi.getQuery().pipe(
      map((queries: { uid: string; name: string }[]) =>
        (queries ?? []).map((q) => ({
          field: 'query',
          value: q.uid,
          name: q.name,
          group: 'Saved query',
        })),
      ),
      catchError(() => of([])),
    );
  }

  private portfolioList(): Observable<TermPickerOption[]> {
    return this.portfolioApi.portFolios().pipe(
      map((portfolios: { uid: string; name: string }[]) =>
        (portfolios ?? []).map((p) => ({
          field: 'portfolio',
          value: p.uid,
          name: p.name,
          group: 'Portfolio',
        })),
      ),
      catchError(() => of([])),
    );
  }

  iDelete() {
    if (window.confirm('Are you sure?')) {
      // this.idelete = 0;
      if (this.queryApi.query.uid === 'default') {
        this.queryApi.delQueries([this.data.uid]).subscribe(() => {
          this.dialog.closeAll();
          this.router.navigate([], {
            queryParams: {
              update: Math.random(),
              queryid: null,
              newsfeedFromQuery: null,
            },
            queryParamsHandling: 'merge',
          });
        });
      } else {
        this.queryApi
          .removeQueriesFromGroup([this.data.uid], this.queryApi.query.uid)
          .subscribe(() => {
            this.dialog.closeAll();
            this.router.navigate([], {
              queryParams: {
                update: Math.random(),
                queryid: null,
                newsfeedFromQuery: null,
              },
              queryParamsHandling: 'merge',
            });
          });
      }
    }
  }

  iSave(update) {
    if (this.namequery.valid) {
      this.insave = false;
      const data = {
        groups: this.selectedCollection,
        name: this.namequery.value,
        description: this.sanitizeInput(this.descriptionQuery.value),
        // Editing can leave an empty group behind; it is not worth sending.
        query: prunedTree(this.tree),
      };
      if (this.data && update) {
        data['uid'] = this.data.uid;
      }
      this.queryApi.addQuery(data).subscribe((result) => {
        this.queryApi.getGroupsQuery().subscribe((groups) => {
          groups.map((group) => {
            if (group.uid === result.uid) {
              this.queryApi.iquery = group.content;
              this.queryApi.query = {
                uid: group.uid,
                name: group.name,
                query: {
                  operator: 'or',
                  filters: [],
                  groups: group.content.map((query) => query.query),
                },
              };
            }
          });
        });
        this.dialog.closeAll();
        this.router.navigate([], {
          queryParams: {
            update: Math.random(),
            queryid: result.uid,
            newsfeedFromQuery: null,
          },
          queryParamsHandling: 'merge',
        });
      });
    }
  }

  iSearch() {
    this.queryApi.query = {
      type: 'search',
      name: this.namequery.value,
      description: this.sanitizeInput(this.descriptionQuery.value),
      query: prunedTree(this.tree),
    };
    writeSearchHandoff(this.queryApi.query);
    if (this.isAiGenerated && this.aiSearchService.lastGeneratedQueryData) {
      this.aiSearchService.lastGeneratedQueryData = {
        ...this.aiSearchService.lastGeneratedQueryData,
        name: this.namequery.value,
        description: this.descriptionQuery.value,
        query: prunedTree(this.tree),
      };
    }
    this.dialog.closeAll();
    let currentView = this.routing.getPathSegment(4);
    if (
      this.isAiGenerated ||
      !currentView ||
      currentView === 'main' ||
      currentView === 'scanner'
    ) {
      currentView = 'chart';
    }
    const queryParams: Record<string, unknown> = {
      id: Math.random(),
      portfolioId: null,
      groupId: null,
      type: null,
      eventid: null,
      eventype: null,
      scoreevent: null,
      newsaudit: null,
      tag: null,
      participant: null,
      score: null,
    };
    if (this.isAiGenerated) {
      queryParams['time'] = '180';
    }
    this.router.navigate(['/cockpit/news/search/' + currentView], {
      queryParams,
      queryParamsHandling: 'merge',
    });
  }

  iExport() {
    if (this.namequery.valid) {
      const queryPure = {
        name: this.namequery.value,
        description: this.descriptionQuery.value,
        query: prunedTree(this.tree),
      };
      const queryString = JSON.stringify(queryPure);
      const blob = new Blob([queryString], { type: 'application/json' });
      FileSaver.saveAs(blob, 'query_' + queryPure.name + '.json');
    }
  }

  /**
   * import query from file
   */
  importQuery(event) {
    if (event.target.files && event.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (e: any) => {
        const result = JSON.parse(e.target.result);
        this.setTree(result.query);
        this.namequery.setValue(result.name);
        this.descriptionQuery.setValue(result.description);
      };
      reader.readAsText(event.target.files[0]);
    }
  }

  sanitizeInput(input: string): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, input) || '';
  }
}
